import React from "react";
import { Box, Typography } from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import DiamondIcon from "@mui/icons-material/Diamond";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import { Link } from "react-router-dom";
import { FEATURES } from "../../config/features";
import { acme, acmeMarketingSectionTitleSx, acmeMarketingEyebrowSx, acmeMarketingBodyLeadSx } from "../../theme/acmeTheme";
import { Card, CardContent, Button } from "../ui";

const sectionEyebrowSx = {
  ...acmeMarketingEyebrowSx,
  color: acme.colors.mutedForegroundSolid,
  fontSize: "0.75rem",
  letterSpacing: "0.12em",
  mb: 1.5,
  display: "block",
};

const sectionTitleSx = {
  ...acmeMarketingSectionTitleSx,
  color: acme.colors.foreground,
  fontSize: { xs: "1.75rem", md: "2.25rem" },
  mb: 1.5,
};

const sectionDescSx = {
  ...acmeMarketingBodyLeadSx,
  color: acme.colors.mutedForegroundSolid,
  maxWidth: 560,
  mx: "auto",
  fontSize: { xs: "0.9375rem", md: "1rem" },
};

const iconWrapSx = {
  width: 48,
  height: 48,
  borderRadius: acme.radius.md,
  backgroundColor: acme.colors.muted,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  mx: "auto",
  mb: 2.5,
  "& .MuiSvgIcon-root": { fontSize: 24, color: acme.colors.iconDefault },
};

const planCardSx = {
  height: "100%",
  boxShadow: "none",
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: acme.colors.mutedForegroundSolid,
  },
};

const planCardPopularSx = {
  ...planCardSx,
  borderColor: acme.colors.brand,
  backgroundColor: `${acme.colors.brand}06`,
};

const guarantees = [
  { icon: VerifiedIcon, title: "100% result", text: "Or money back" },
  { icon: AccessTimeIcon, title: "Start today", text: "First results in 7 days" },
  { icon: IntegrationInstructionsIcon, title: "Ready solutions", text: "Integration with your systems in 1 hour" },
];

export default function HomeOffersAndTrustSection() {
  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: acme.colors.pageBg }}>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 } }}>
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 7 } }}>
          <Typography component="span" sx={sectionEyebrowSx}>
            Get started
          </Typography>
          <Typography component="h2" sx={sectionTitleSx}>
            Free setup of your first campaign
          </Typography>
          <Typography sx={sectionDescSx}>
            Choose your path to success — we&apos;ll find the perfect solution for your needs
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2.5,
            mb: { xs: 5, md: 6 },
          }}
        >
          <Card sx={planCardSx}>
            <CardContent sx={{ textAlign: "center", p: { xs: 3, md: 3.5 } }}>
              <Box sx={iconWrapSx}>
                <RocketLaunchIcon />
              </Box>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem" }}>
                Do It Yourself
              </Typography>
              <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5, lineHeight: 1.65, fontSize: "0.9375rem" }}>
                Purchase a plan and manage your campaigns independently
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1.75rem", mb: 0.25, color: acme.colors.foreground }}>
                Free
              </Typography>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5 }}>
                setup and first campaign
              </Typography>
              <Button component={Link} to="/pricing" variant="outline" fullWidth>
                Choose plan
              </Button>
            </CardContent>
          </Card>

          <Card sx={{ ...planCardPopularSx, position: "relative", overflow: "visible" }}>
            <Box
              sx={{
                position: "absolute",
                top: -10,
                left: "50%",
                transform: "translateX(-50%)",
                px: 1.5,
                py: 0.35,
                borderRadius: acme.radius.full,
                backgroundColor: acme.colors.brandMuted,
                color: acme.colors.brand,
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                border: `1px solid ${acme.colors.brand}33`,
              }}
            >
              Popular
            </Box>
            <CardContent sx={{ textAlign: "center", p: { xs: 3, md: 3.5 } }}>
              <Box sx={{ ...iconWrapSx, backgroundColor: acme.colors.brandMuted, "& .MuiSvgIcon-root": { color: acme.colors.brand } }}>
                <LightbulbIcon />
              </Box>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem" }}>
                On-Demand Help
              </Typography>
              <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5, lineHeight: 1.65, fontSize: "0.9375rem" }}>
                We create emails, set up campaigns, and show you analytics
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1.75rem", mb: 0.25, color: acme.colors.foreground }}>
                €50
              </Typography>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5 }}>
                per campaign
              </Typography>
              <Button component={Link} to={FEATURES.PROJECTS_ENABLED ? "/requests" : "/pricing"} fullWidth>
                Order help
              </Button>
            </CardContent>
          </Card>

          <Card sx={planCardSx}>
            <CardContent sx={{ textAlign: "center", p: { xs: 3, md: 3.5 } }}>
              <Box sx={iconWrapSx}>
                <DiamondIcon />
              </Box>
              <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem" }}>
                Full Management
              </Typography>
              <Typography sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5, lineHeight: 1.65, fontSize: "0.9375rem" }}>
                We do everything: emails, campaigns, analytics. You get results
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: "1.75rem", mb: 0.25, color: acme.colors.foreground }}>
                €200
              </Typography>
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, mb: 2.5 }}>
                per month
              </Typography>
              <Button
                component={Link}
                to={FEATURES.PROJECTS_ENABLED ? "/requests" : "/pricing"}
                variant="outline"
                fullWidth
              >
                Get management
              </Button>
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              mb: 2.5,
              textAlign: "center",
              fontSize: "1.0625rem",
              color: acme.colors.foreground,
            }}
          >
            Our guarantees
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {guarantees.map(({ icon: Icon, title, text }) => (
              <Card key={title} sx={{ boxShadow: "none" }}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: acme.radius.full,
                      backgroundColor: acme.colors.muted,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 1.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: acme.colors.iconDefault }} />
                  </Box>
                  <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "0.9375rem" }}>{title}</Typography>
                  <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid, lineHeight: 1.55 }}>
                    {text}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
